// Heuristic detection of an in-app / embedded browser (Instagram, Facebook,
// TikTok, LINE, etc.). These WebViews frequently break the OAuth redirect flow:
// Google blocks OAuth in embedded WebViews ("disallowed_useragent"), and the
// PKCE code_verifier can be lost when the return re-opens an external browser
// (a different storage context), so signInWithOAuth never completes.
//
// Used only to TAG analytics (oauth_started.is_webview) so the OAuth funnel can
// be split by environment. Read-only: navigator.userAgent, no dependency, no
// side effects. Conservative — returns true only on a recognized signal.

// Literal UA substrings emitted by well-known in-app browsers (case-sensitive).
const IN_APP_UA_TOKENS = [
  "FBAN", "FBAV", "FB_IAB",                    // Facebook / Messenger
  "Instagram",                                  // Instagram
  "TikTok", "musical_ly", "BytedanceWebview",  // TikTok
  "Line/",                                      // LINE
  "Snapchat",                                   // Snapchat
  "Twitter",                                     // Twitter / X
  "LinkedInApp",                                // LinkedIn
  "Pinterest",                                   // Pinterest
  "WhatsApp",                                    // WhatsApp
  "GSA/",                                        // Google Search App in-app browser
  "MicroMessenger",                             // WeChat
];

export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (!ua) return false;

  // 1. Explicit in-app browser UA tokens.
  if (IN_APP_UA_TOKENS.some((tok) => ua.includes(tok))) return true;

  // 2. Android System WebView marker — "; wv)" appears in an Android WebView's
  //    UA (not in Chrome). Embedded browsers ride on it.
  if (/;\s*wv\)/.test(ua)) return true;

  // 3. iOS WKWebView heuristic — an in-app browser on iOS reports AppleWebKit +
  //    Mobile but omits "Safari" and the known iOS browser tokens (CriOS =
  //    Chrome, FxiOS = Firefox, EdgiOS = Edge). Real Safari always includes
  //    "Safari".
  if (/iPhone|iPad|iPod/.test(ua)) {
    const isRealBrowser = /Safari|CriOS|FxiOS|EdgiOS/.test(ua);
    if (/AppleWebKit/.test(ua) && /Mobile/.test(ua) && !isRealBrowser) return true;
  }

  return false;
}
